import { Link } from 'react-router-dom';
import { benefits } from '@/mocks/homeData';
import { useTranslation } from 'react-i18next';

const BENEFIT_KEYS = [
  'item0',
  'item1',
  'item2',
  'item3',
  'item4',
] as const;

export default function BenefitsSection() {
  const { t } = useTranslation();
  return (
    <section className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
      {/* Decorative element */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-accent-500/[0.02] rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            BENEFITS & CERTIFICATE
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 tracking-[0.06em] mb-4">
            {t('home.benefits.title')}
          </h2>
          <div className="gold-line mx-auto mb-4"></div>
          {String(t('home.benefits.subtitle')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em] max-w-xl mx-auto leading-relaxed">{__s}</p>))}
        </div>

        {/* Main Content - Split Layout */}
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Left: Image */}
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src="/images/generated/tjba26_b02_gloves_before_mirror_r2.jpg"
                  alt={t('home.benefits.imgAlt')}
                  title={t('home.benefits.imgTitle')}
                  className="w-full object-cover object-top"
                />
              </div>
              {/* Accent line */}
              <div className="absolute -bottom-3 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-accent-500/30 to-transparent"></div>
            </div>
          </div>

          {/* Right: Benefits List */}
          <div className="w-full lg:w-7/12">
            <div className="space-y-6">
              {benefits.map((benefit, index) => {
                const key = BENEFIT_KEYS[index];
                return (
                  <div key={index} className="flex gap-5 items-start group pb-5 border-b border-background-200/80 last:border-b-0">
                    <div className="w-11 h-11 flex items-center justify-center flex-shrink-0">
                      <i className={`${benefit.icon} text-xl text-accent-600 group-hover:text-accent-500 transition-colors duration-300`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-1.5">
                        {t(`home.benefits.${key}.title`)}
                      </h4>
                      <p className="text-[15px] text-foreground-600 leading-relaxed tracking-[0.03em]">
                        {t(`home.benefits.${key}.desc`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-10">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.08em] transition-colors duration-300 group"
              >
                <span>{t('home.benefits.cta')}</span>
                <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
